'use client';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { CheckIcon } from 'lucide-react';
import type {
  ErrandSubTypeContext,
  ErrandTypeContext,
} from '../../[id]/hooks/use-get-errand-context';

interface ErrandTypeStepProps {
  readonly errandTypes: ErrandTypeContext[];
  readonly subTypes: ErrandSubTypeContext[];
  readonly selectedTypeId: string | null;
  readonly selectedSubTypeId: string | null;
  readonly isSubTypeRequired: boolean;
  readonly onSelect: (typeId: string) => void;
  readonly onSelectSubType: (subTypeId: string | null) => void;
}

export function ErrandTypeStep({
  errandTypes,
  subTypes,
  selectedTypeId,
  selectedSubTypeId,
  isSubTypeRequired,
  onSelect,
  onSelectSubType,
}: ErrandTypeStepProps) {
  return (
    <div className='space-y-4'>
      <div>
        <h2 className='text-lg font-semibold'>Välj ärendetyp</h2>
        <p className='text-sm text-muted-foreground'>
          Välj vilken typ av ärende du vill rapportera.
        </p>
      </div>

      <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'>
        {errandTypes
          .toSorted((a, b) => a.name.localeCompare(b.name, 'sv'))
          .map((type) => {
            const isSelected = type.id === selectedTypeId;
            return (
              <button
                key={type.id}
                type='button'
                onClick={() => onSelect(type.id)}
                className={cn(
                  'relative rounded-lg border-2 p-4 text-left transition-colors',
                  isSelected
                    ? 'border-foreground bg-foreground/10 text-foreground'
                    : 'border-border bg-card hover:border-foreground/50 hover:bg-foreground/5'
                )}
              >
                {isSelected && (
                  <div className='absolute top-3 right-3'>
                    <CheckIcon className='size-5 text-foreground' />
                  </div>
                )}
                <span className='text-sm font-medium'>{type.name}</span>
              </button>
            );
          })}
      </div>

      {errandTypes.length === 0 && (
        <p className='text-sm text-muted-foreground'>
          Inga ärendetyper tillgängliga.
        </p>
      )}

      {selectedTypeId && subTypes.length > 0 && (
        <div className='space-y-4 pt-4'>
          <div>
            <h3 className='text-base font-medium'>
              Välj undertyp{' '}
              {isSubTypeRequired && <span className='text-destructive'>*</span>}
            </h3>
            <p className='text-sm text-muted-foreground'>
              Välj den undertyp som passar bäst.
            </p>
          </div>

          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'>
            {subTypes.map((subType) => {
              const isSelected = subType.id === selectedSubTypeId;
              return (
                <button
                  key={subType.id}
                  type='button'
                  onClick={() => onSelectSubType(subType.id)}
                  className={cn(
                    'relative rounded-lg border-2 p-4 text-left transition-colors',
                    isSelected
                      ? 'border-foreground bg-foreground/10 text-foreground'
                      : 'border-border bg-card hover:border-foreground/50 hover:bg-foreground/5'
                  )}
                >
                  {isSelected && (
                    <div className='absolute top-3 right-3'>
                      <CheckIcon className='size-5 text-foreground' />
                    </div>
                  )}
                  <span className='text-sm font-medium'>{subType.name}</span>
                </button>
              );
            })}
          </div>

          {!isSubTypeRequired && (
            <Button
              type='button'
              className='border-foreground bg-foreground hover:bg-foreground/80'
              onClick={() => onSelectSubType(null)}
            >
              Fortsätt utan undertyp
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
